import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Drawer } from '../../../components/ui/Drawer';
import { Badge } from '../../../components/ui/Badge';
import { STATUS_MAP, getStep, fmtEur, fmtDate } from '../utils';
import { ProgressBar } from './ProgressBar';
import { FieldRow } from './FieldRow';

export function ClaimDrawer({ claim, onClose }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  if (!claim) return null;

  const isSinistre = claim.claimType === 'sinistre';
  const { current, total } = getStep(claim.status, isSinistre);
  const statusDef  = STATUS_MAP[claim.status] || STATUS_MAP.open;

  return (
    <Drawer
      open={!!claim}
      onClose={onClose}
      width="w-[580px]"
      headerContent={
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[13px] font-semibold text-slate-900">{claim.reference || '—'}</span>
            <Badge variant={statusDef.variant}>{t(`claims.${statusDef.labelKey}`)}</Badge>
          </div>
          <p className="text-[12px] text-slate-500 mt-0.5">{claim.subject || '—'}</p>
        </div>
      }
      footer={
        <div className="flex items-center justify-between w-full">
          <span className="text-[12px] text-slate-500">
            {claim.insurer && `${t('claims.insurer')} ${claim.insurer}`}
          </span>
          <button className="h-8 px-3 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-medium text-slate-600 hover:bg-slate-50 transition">
            <Download size={13} strokeWidth={1.75} />
            {t('claims.drawer_docs_btn')}
          </button>
        </div>
      }
    >
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-[12px] font-semibold text-slate-700 mb-3">{t('claims.drawer_progress')}</p>
        <ProgressBar current={current} total={total} isSinistre={isSinistre} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
            {t('claims.kpi_total_claimed')}
          </p>
          <p className="text-[20px] font-semibold tabular-nums text-slate-900">
            {fmtEur(claim.claimedAmountEur)}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
            {t('claims.drawer_approved_amount')}
          </p>
          <p className="text-[20px] font-semibold tabular-nums text-slate-900">
            {fmtEur(claim.approvedAmountEur)}
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-[13px] font-semibold text-slate-900 mb-2">{t('claims.drawer_details')}</p>
        <FieldRow label={t('claims.drawer_client')}          value={claim.companyName} />
        <FieldRow label={t('claims.drawer_opened')}          value={fmtDate(claim.openedAt, locale)} />
        <FieldRow label={t('claims.drawer_closed')}          value={fmtDate(claim.closedAt, locale)} />
        <FieldRow label={t('claims.drawer_place')}           value={claim.disasterPlace} />
        <FieldRow label={t('claims.drawer_claim_type')}      value={claim.manifestedClaimType} />
        <FieldRow label={t('claims.drawer_transport_unit')}  value={claim.transportUnitSerialNo} />
        <FieldRow label={t('claims.drawer_freight_vendor')}  value={claim.freightVendorName} />
        <FieldRow label={t('claims.drawer_incoterm')}        value={claim.incoterm} />
        {isSinistre && (
          <>
            <FieldRow label={t('claims.drawer_insurer')}      value={claim.insurer} />
            <FieldRow
              label={t('claims.drawer_expertise')}
              value={claim.expertisePerformed != null ? (claim.expertisePerformed ? t('common.active') : 'Non') : null}
            />
            <FieldRow
              label={t('claims.drawer_depreciation')}
              value={claim.depreciationRate != null ? `${claim.depreciationRate}%` : null}
            />
            <FieldRow label={t('claims.drawer_valuation')} value={fmtEur(claim.valuationAmount)} />
          </>
        )}
        {claim.parcelsCount != null && (
          <FieldRow label={t('claims.drawer_parcels')} value={`${claim.parcelsClaimed ?? '?'} / ${claim.parcelsCount}`} />
        )}
        {claim.admissible != null && (
          <FieldRow label={t('claims.drawer_admissible')} value={claim.admissible ? 'Oui' : 'Non'} />
        )}
        {claim.reasonOfRejection && (
          <FieldRow label={t('claims.drawer_rejection_reason')} value={claim.reasonOfRejection} />
        )}
      </div>

      {claim.attachments?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[13px] font-semibold text-slate-900 mb-3">{t('claims.drawer_attachments')}</p>
          <div className="space-y-2">
            {claim.attachments.map((att, i) => {
              const name = att.name || att.filename || `Document ${i + 1}`;
              return (
                <div key={i} className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-xl p-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[12.5px] font-semibold text-slate-900 truncate">{name}</p>
                  </div>
                  {att.url && (
                    <a href={att.url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-primary transition-colors">
                      <Download size={14} strokeWidth={1.75} />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Drawer>
  );
}
